/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

import { IActionViewItem } from '../../../base/browser/ui/actionbar/actionbar.js';
import { IActionViewItemOptions } from '../../../base/browser/ui/actionbar/actionViewItems.js';
import { IAction } from '../../../base/common/actions.js';
import { Emitter, Event } from '../../../base/common/event.js';
import { InstantiationType, registerSingleton } from '../../instantiation/common/extensions.js';
import { createDecorator, IInstantiationService } from '../../instantiation/common/instantiation.js';
import { MenuId } from '../common/actions.js';


export const IActionViewItemService = createDecorator<IActionViewItemService>('IActionViewItemService');


export interface IActionViewItemFactory {
	(action: IAction, options: IActionViewItemOptions, instantiationService: IInstantiationService, windowId: number): IActionViewItem | undefined;
}

export interface IActionViewItemService {

	_serviceBrand: undefined;

	onDidChange: Event<MenuId>;

	lookUp(menu: MenuId, submenu: MenuId): IActionViewItemFactory | undefined;
	lookUp(menu: MenuId, commandId: string): IActionViewItemFactory | undefined;
}

class ActionViewItemService implements IActionViewItemService {

	declare _serviceBrand: undefined;

	private readonly _providers = new Map<string, IActionViewItemFactory>();

	private readonly _onDidChange = new Emitter<MenuId>();
	readonly onDidChange: Event<MenuId> = this._onDidChange.event;

	dispose(): void {
		this._onDidChange.dispose();
	}

	lookUp(menu: MenuId, commandOrMenuId: string | MenuId): IActionViewItemFactory | undefined {
		return this._providers.get(this._makeKey(menu, commandOrMenuId));
	}

	private _makeKey(menu: MenuId, commandOrMenuId: string | MenuId) {
		return `${menu.id}/${(commandOrMenuId instanceof MenuId ? commandOrMenuId.id : commandOrMenuId)}`;
	}
}

registerSingleton(IActionViewItemService, ActionViewItemService, InstantiationType.Delayed);
